/**
 * The Agents tab: the extractors, what they have done, and what is configured.
 *
 * Agents are configuration, not a discovery step — the routing table decides
 * which one runs. When no language model is configured the tab says so plainly,
 * rather than leaving an idle agent to look broken.
 */

import { useState } from 'react'
import { EmptyState, Loading, Modal } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip, Field, Mark } from '../components'
import { PipelinePanel } from './PipelinePanel'
import { useAgentMutations, useAgents } from '../hooks'
import type { Agent, GraphModel } from '../types/brain.types'

export function AgentsTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const agents = useAgents(graphId)
  const { remove } = useAgentMutations(graphId)
  const toast = useToast()
  const [editing, setEditing] = useState<Agent | 'new' | null>(null)

  if (agents.isLoading) return <Loading label="Loading agents…" />

  const providers = agents.data?.providers
  const rows = agents.data?.agents ?? []

  return (
    <div className="brain-panel">
      <div className="brain-colstack">
        <div className="cp-toolbar">
          <h1>Agents</h1>
          <Chip>
            {rows.length} agent{rows.length === 1 ? '' : 's'}
          </Chip>
          {providers && (
            <Chip tone={providers.llm.configured ? 'ok' : 'warn'} dot={providers.llm.configured}>
              {providers.llm.configured ? providers.llm.default_model : 'no model configured'}
            </Chip>
          )}
          <span className="cp-spacer" />
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            + New agent
          </button>
          <div className="sub2">
            Schema-driven extractors grounded in this graph — they read a source's records and
            report only the node types and relationships the model already defines. Which one runs
            is decided by the source's mode, never by a step that inspects the data first.
          </div>
        </div>

        {providers && !providers.llm.configured && (
          <div className="callout callout-warn">
            <p className="callout-title">No language model is configured</p>
            <p>
              Sources set to <code>direct_map</code> keep working — they never call a model.
              Sources set to <code>extract</code>, and the Ask panel, need one: set{' '}
              <code>GROQ_API_KEY</code> or <code>HF_TOKEN</code> on the API service. Groq's
              free tier is what this project defaults to, and with either key alone the
              model names resolve themselves — <code>LLM_DEFAULT_MODEL</code> only needs
              setting to override them. <code>GEMINI_API_KEY</code>,{' '}
              <code>OPENAI_API_KEY</code> and <code>NVIDIA_API_KEY</code> work too, and Groq
              wins if more than one is set.
            </p>
            <p className="small">
              Already set them? The API reads its environment once at startup — restart it, and
              if you are pointed at a deployed backend, set the variable there rather than in a
              local <code>.env</code>.
            </p>
          </div>
        )}

        {providers && (
          <div className="row" style={{ gap: 18 }}>
            <div>
              <div className="small faint">Language model</div>
              <div className="mono small">
                {providers.llm.configured ? providers.llm.default_model : 'not configured'}
              </div>
            </div>
            <div>
              <div className="small faint">Fallback</div>
              <div className="mono small">{providers.llm.fallback_model ?? '—'}</div>
            </div>
            <div>
              <div className="small faint">Embeddings</div>
              <div className="mono small">
                {providers.embeddings.model} · {providers.embeddings.dimensions}d
              </div>
            </div>
          </div>
        )}

        {providers && !providers.embeddings.configured && (
          <p className="small faint" style={{ margin: 0 }}>
            Search is running on the local encoder, which matches on wording rather than meaning.
            Set <code>CF_ACCOUNT_ID</code> and <code>CF_AI_TOKEN</code> for real embeddings.
          </p>
        )}

        {rows.length === 0 ? (
          <EmptyState title="No agents yet">
            The default extractor appears the first time a source runs. You can also add one now
            and scope it to particular node types.
          </EmptyState>
        ) : (
          <div className="cp-card cp-tablewrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Mode</th>
                  <th>Model</th>
                  <th>Prompt</th>
                  <th>Graph access</th>
                  <th>Runs</th>
                  <th>Produced</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((agent) => (
                  <tr key={agent.agent_id}>
                    <td>
                      <span className="cp-rowtitle">
                        <Mark name={agent.display_name || agent.key} id={agent.agent_id} />
                        <span className="mono truncate">{agent.key}</span>
                      </span>
                      {agent.description && (
                        <div className="small faint" style={{ maxWidth: 280, marginTop: 2 }}>
                          {agent.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <Chip>{agent.mode}</Chip>
                    </td>
                    <td className="mono small" style={{ color: 'var(--derived)' }}>
                      {agent.mode === 'direct_map' ? '— (no model)' : agent.model || '—'}
                    </td>
                    <td className="mono small">{agent.prompt_version}</td>
                    <td>
                      {agent.node_type_keys.length ? (
                        <span className="row-tight" style={{ flexWrap: 'wrap', gap: 4 }}>
                          {agent.node_type_keys.map((key) => (
                            <span key={key} className="flag">
                              {key}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="faint small">every node type</span>
                      )}
                    </td>
                    <td className="num">
                      {agent.runs}
                      {agent.failures > 0 && (
                        <span style={{ marginLeft: 6 }}>
                          <Chip tone="err">{agent.failures} failed</Chip>
                        </span>
                      )}
                    </td>
                    <td className="num">{agent.records_produced.toLocaleString()}</td>
                    <td>
                      <Chip
                        tone={
                          agent.status === 'error' ? 'err' : agent.status === 'ok' ? 'ok' : 'plain'
                        }
                        dot={agent.status === 'ok'}
                        title={agent.last_error ?? undefined}
                      >
                        {agent.status === 'idle' ? 'never run' : agent.status}
                      </Chip>
                    </td>
                    <td>
                      <div className="row-tight">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(agent)}>
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            if (!window.confirm(`Delete '${agent.key}'?`)) return
                            try {
                              await remove.mutateAsync(agent.agent_id)
                              toast.success('Agent deleted.')
                            } catch (error) {
                              toast.error(error)
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PipelinePanel graphId={graphId} />
      </div>

      {editing && (
        <AgentDialog
          graphId={graphId}
          model={model}
          existing={editing === 'new' ? undefined : editing}
          defaultModel={providers?.llm.default_model ?? ''}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function AgentDialog({
  graphId,
  model,
  existing,
  defaultModel,
  onClose,
}: {
  graphId: string
  model: GraphModel
  existing?: Agent
  defaultModel: string
  onClose: () => void
}) {
  const { create, update } = useAgentMutations(graphId)
  const toast = useToast()

  const [key, setKey] = useState(existing?.key ?? '')
  const [displayName, setDisplayName] = useState(existing?.display_name ?? '')
  const [mode, setMode] = useState(existing?.mode ?? 'extract')
  const [modelName, setModelName] = useState(existing?.model ?? defaultModel)
  const [scope, setScope] = useState<string[]>(existing?.node_type_keys ?? [])
  const [instructions, setInstructions] = useState(existing?.instructions ?? '')

  const busy = create.isPending || update.isPending

  return (
    <Modal
      title={existing ? `Edit ${existing.key}` : 'New agent'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || (!existing && !key.trim())}
            onClick={async () => {
              const body = {
                display_name: displayName.trim() || key.trim(),
                model: modelName.trim() || null,
                node_type_keys: scope,
                instructions: instructions.trim() || null,
              }
              try {
                if (existing) await update.mutateAsync({ id: existing.agent_id, body })
                else await create.mutateAsync({ ...body, key: key.trim(), mode })
                toast.success('Agent saved.')
                onClose()
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        {!existing && (
          <div className="field-pair">
            <Field label="Key" required>
              <input
                className="input"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="contract_specialist"
              />
            </Field>
            <Field label="Mode" help="direct_map never calls a model; extract reads text.">
              <select
                className="select"
                value={mode}
                onChange={(e) => setMode(e.target.value as Agent['mode'])}
              >
                <option value="extract">extract</option>
                <option value="direct_map">direct_map</option>
              </select>
            </Field>
          </div>
        )}

        <Field label="Display name">
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>

        {mode === 'extract' && (
          <>
            <Field label="Model" help="Provider-prefixed, e.g. groq/openai/gpt-oss-20b. Blank uses the default.">
              <input
                className="input"
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
              />
            </Field>

            <Field
              label="Only these node types"
              help="Leave everything unchecked to allow any type in the model."
            >
              <div className="chiprow">
                {model.node_types.map((node) => {
                  const on = scope.includes(node.key)
                  return (
                    <button
                      key={node.node_type_id}
                      className="chip-filter"
                      data-on={on}
                      onClick={() =>
                        setScope((list) =>
                          on ? list.filter((k) => k !== node.key) : [...list, node.key],
                        )
                      }
                    >
                      {node.key}
                    </button>
                  )
                })}
              </div>
            </Field>

            <Field
              label="Extra guidance"
              help="Appended to the extractor's instructions — domain vocabulary, or what to ignore."
            >
              <textarea
                className="textarea"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="'ARR' in our mail always means annual recurring revenue in USD."
              />
            </Field>
          </>
        )}
      </div>
    </Modal>
  )
}
